import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, X, Send, Plus, Loader2, Sparkles, Mail } from "lucide-react";
import { API_BASE } from "@/lib/apiBase";

const SUGGESTIONS = {
  admin: ["Summarize this week's settlements", "Draft an email to partners about Wednesday payouts", "Any investor items needing attention?"],
  investor: ["What vehicles am I assigned?", "Explain my latest statement", "What documents should I review?"],
  partner: ["What's on my schedule today?", "Summarize my last settlement", "How are my upsells doing?"],
  public: ["What vehicles are available?", "How does the revenue share work?", "What does a 25% tier mean?"],
};

export function Copilot({ audience = "investor" }) {
  const isPublic = audience === "public";
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [compose, setCompose] = useState(null); // {to, subject, body}
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const titles = { admin: "XE Copilot", investor: "Investor Assistant", partner: "Partner Assistant", public: "Ask About Investing" };
  const placeholders = {
    admin: "Ask about operations, draft an email…",
    investor: "Ask about your vehicles, statements, documents…",
    partner: "Ask about reservations, settlements, upsells…",
    public: "Ask about the vehicle opportunities…",
  };

  const chatUrl = `${API_BASE}/copilot/${isPublic ? "public-chat" : "chat"}`;

  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API_BASE}/copilot/send-email`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(compose),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Send failed");
      toast.success(`Email sent to ${compose.to}`);
      setCompose(null);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setSending(false); }
  };

  useEffect(() => {
    if (!open || isPublic) return;
    api_list();
  }, [open]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const api_list = async () => {
    try {
      const r = await fetch(`${API_BASE}/copilot/sessions`, { credentials: "include" });
      if (r.ok) setSessions(await r.json());
    } catch { /* ignore */ }
  };

  const loadSession = async (sid) => {
    try {
      const r = await fetch(`${API_BASE}/copilot/sessions/${sid}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSessionId(sid);
        setMessages(d.messages || []);
      }
    } catch { /* ignore */ }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const r = await fetch(chatUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Copilot unavailable");
      const sid = r.headers.get("x-session-id");
      if (sid) setSessionId(sid);
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
      if (!isPublic) api_list();
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `Error: ${e.message}` };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="copilot-toggle"
        className="fixed bottom-6 right-6 z-[70] grid h-14 w-14 place-items-center rounded-full bg-offwhite text-ink shadow-[0_10px_40px_-10px_rgba(10,14,16,0.5)] transition-transform hover:scale-105"
        aria-label="AI Copilot">
        {open ? <X size={20} /> : <Sparkles size={20} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-[70] flex h-[520px] w-[92vw] max-w-[400px] flex-col overflow-hidden rounded-2xl border border-black/10 bg-white shadow-2xl" data-testid="copilot-panel">
          <div className="flex items-center justify-between border-b border-black/[0.06] bg-offwhite px-4 py-3">
            <p className="font-display text-sm font-extrabold text-ink">{titles[audience] || "Assistant"}</p>
            <button onClick={newChat} className="flex items-center gap-1 text-[11px] font-bold text-white/80 hover:text-white" data-testid="copilot-new">
              <Plus size={13} /> New
            </button>
          </div>

          {!messages.length && (
            <div className="border-b border-black/[0.06] px-4 py-3">
              {!isPublic && sessions.length > 0 && (
                <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1">
                  {sessions.slice(0, 5).map((s) => (
                    <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                      className="whitespace-nowrap rounded-full border border-black/10 px-2.5 py-1 text-[10px] text-muted hover:border-aqua/50"
                      data-testid={`copilot-session-${s.session_id}`}>
                      {s.title}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS[audience].map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={busy}
                    className="rounded-full bg-surface px-3 py-1.5 text-[11px] font-semibold text-offwhite hover:bg-aqua/20"
                    data-testid={`copilot-suggestion-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3" data-testid="copilot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-offwhite text-white" : "bg-surface text-offwhite"}`}>
                {m.content || (busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin" /> : "")}
                {audience === "admin" && m.role === "assistant" && m.content.length > 80 && !busy && (
                  <button
                    onClick={() => setCompose({ to: "", subject: "", body: m.content.replace(/\*\*/g, "") })}
                    className="mt-2 flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3 py-1 text-[10px] font-bold text-offwhite hover:border-aqua"
                    data-testid={`copilot-email-${i}`}>
                    <Mail size={11} /> Send as email
                  </button>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="flex items-center gap-2 border-t border-black/[0.06] p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={placeholders[audience] || "Ask…"}
              className="field !rounded-full !py-2.5 !text-sm" data-testid="copilot-input" />
            <button onClick={() => send()} disabled={busy || !input.trim()}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-offwhite text-ink disabled:opacity-40"
              data-testid="copilot-send">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </div>
        </div>
      )}

      {compose && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-black/40 p-4" data-testid="copilot-compose">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-extrabold text-offwhite">Send email</h3>
              <button onClick={() => setCompose(null)} className="text-muted hover:text-offwhite" data-testid="compose-close"><X size={18} /></button>
            </div>
            <p className="mt-1 text-[11px] text-muted">Review and edit before sending — goes out from your Xtreme Exotics mailbox.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="field-label" htmlFor="compose-to">To</label>
                <input id="compose-to" className="field !py-2.5 !text-sm" placeholder="partner@example.com" value={compose.to}
                  onChange={(e) => setCompose({ ...compose, to: e.target.value })} data-testid="compose-to" />
              </div>
              <div>
                <label className="field-label" htmlFor="compose-subject">Subject</label>
                <input id="compose-subject" className="field !py-2.5 !text-sm" value={compose.subject}
                  onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="compose-subject" />
              </div>
              <div>
                <label className="field-label" htmlFor="compose-body">Message</label>
                <textarea id="compose-body" className="field min-h-[160px] !text-sm" value={compose.body}
                  onChange={(e) => setCompose({ ...compose, body: e.target.value })} data-testid="compose-body" />
              </div>
              <button onClick={sendEmail} disabled={sending || !compose.to.includes("@") || !compose.subject.trim() || !compose.body.trim()}
                className="btn-primary w-full !py-3 !text-xs disabled:opacity-50" data-testid="compose-send-btn">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <><Mail size={13} /> Send Email</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
